import { fallbackSpeechLanguage } from '@/core/captions/language';
import { wordsToCues } from '@/core/captions/segment';
import { applyPreset, type PresetSettings } from '@/core/project/preset';
import { DEFAULT_OUTPUT, type OutputFormatId } from '@/core/project/output-format';
import { createDefaultRecipe, type EditRecipe } from '@/core/project/recipe';
import type { Track } from '@/core/tracking/track';
import { engine, type Task } from '@/engine/client';
import { EngineError } from '@/engine/errors';
import type { AudioAnalysis, ExportResult, MediaInfo } from '@/engine/types';
import { outputFileName } from '@/state/editor-store';
import { telemetrySources } from '@/state/intake';
import { usePlacesStore } from '@/state/places-store';
import { withPreferredUnits } from '@/lib/units';
import type { Telemetry } from '@/core/telemetry/telemetry';
import { selectExportJob, selectKept } from '@/state/selectors';
import { keptAssetRanges, PRIMARY_ASSET } from '@/core/project/sequence';
import { scanRanges } from '@/core/detection/scan-plan';
import { range } from '@/core/time/ranges';
import { notificationTracks } from '@/core/screen/notifications';
import type { ScreenAnalysis } from '@/engine/analysis/screen-activity';

/** What each file is saved as: a format, and optionally "keep it under N MB". */
export interface BatchOutput {
  readonly output?: OutputFormatId;
  readonly sizeLimit?: number | null;
}

export type BatchStep = 'probe' | 'audio' | 'scan' | 'screen' | 'captions' | 'export';

export interface BatchProgress {
  readonly step: BatchStep;
  /** Overall progress of this file, 0..1. */
  readonly ratio: number;
}

/** Relative cost of each step, used to turn step progress into one bar. */
const WEIGHTS: Readonly<Record<BatchStep, number>> = { probe: 0.5, audio: 2, scan: 10, screen: 4, captions: 8, export: 12 };

/**
 * Runs a preset on one file without the editor: probe → analyses the preset
 * needs → export, saved as `output`. Uses the same selectors as the editor, so a batch result is
 * identical to exporting the same video by hand with the same settings.
 */
export async function processWithPreset(
  file: File,
  settings: PresetSettings,
  onProgress: (progress: BatchProgress) => void,
  register: (task: Task<unknown>) => void,
  { output = DEFAULT_OUTPUT, sizeLimit = null }: BatchOutput = {},
): Promise<ExportResult> {
  const run = async <T>(task: Task<T>): Promise<T> => {
    register(task as Task<unknown>);
    return task.promise;
  };

  onProgress({ step: 'probe', ratio: 0 });
  const media: MediaInfo = await run(engine.probe(file));
  const audioOk = media.audio?.decodable ?? false;
  const needsAudio = audioOk && (settings.silence.enabled || settings.audio.normalize);
  const scanKinds = settings.privacy.enabled ? settings.privacy.scan : [];
  const transcribe = audioOk && settings.captions.auto;

  // Auto zoom needs to know where the screen changes; the same pass finds notifications to hide.
  const analyzeScreen = settings.zoom.enabled;
  const steps: BatchStep[] = [
    'probe',
    ...(needsAudio ? (['audio'] as const) : []),
    ...(analyzeScreen ? (['screen'] as const) : []),
    ...(transcribe ? (['captions'] as const) : []),
    // Last before saving: by then it is known which parts are cut, and only the rest is scanned.
    ...(scanKinds.length ? (['scan'] as const) : []),
    'export',
  ];
  const total = steps.reduce((sum, step) => sum + WEIGHTS[step], 0);
  const report = (step: BatchStep, ratio: number) => {
    const before = steps.slice(0, steps.indexOf(step)).reduce((sum, s) => sum + WEIGHTS[s], 0);
    onProgress({ step, ratio: Math.min(1, (before + WEIGHTS[step] * Math.min(1, Math.max(0, ratio))) / total) });
  };

  let analysis: AudioAnalysis | null = null;
  if (needsAudio) {
    report('audio', 0);
    analysis = await run(engine.analyzeAudio(file, (p) => report('audio', p.ratio)));
  }

  let screen: ScreenAnalysis | null = null;
  if (analyzeScreen) {
    report('screen', 0);
    screen = await run(engine.analyzeScreen(file, (p) => report('screen', p.ratio)));
  }

  // The GPS track is tiny next to the video, so reading it is not worth its own progress step.
  // It is read whenever it could matter: for the gauge, or to keep private places out.
  const places = usePlacesStore.getState().places;
  let telemetry: Telemetry | null = null;
  if (settings.telemetry.enabled || (settings.zones.enabled && (settings.zones.hideEnds || places.length > 0))) {
    const result = await run(engine.readTelemetry(telemetrySources(file)));
    telemetry = result.status === 'ok' ? result.telemetry : null;
  }

  let recipe: EditRecipe = { ...applyPreset(withPreferredUnits(createDefaultRecipe()), settings), output, sizeLimit };
  if (screen && screen.notifications.length > 0 && settings.privacy.enabled) {
    recipe = { ...recipe, privacy: { ...recipe.privacy, manualTracks: notificationTracks(screen.notifications) } };
  }
  if (transcribe) {
    report('captions', 0);
    const { words } = await run(
      engine.transcribe(
        file,
        { language: settings.captions.language, quality: settings.captions.quality, fallbackLanguage: fallbackSpeechLanguage(navigator.languages) },
        (p) => report('captions', p.phase === 'scan' ? p.ratio : 0),
      ),
    );
    const cues = wordsToCues(words);
    recipe = { ...recipe, captions: { ...recipe.captions, cues, enabled: settings.captions.enabled && cues.length > 0 } };
  }

  const { row, keep } = selectKept({ media, recipe, soundOf: () => analysis, telemetry, places });
  if (keep.length === 0) throw new EngineError('empty-output');

  let tracks: Track[] = [];
  if (scanKinds.length > 0) {
    report('scan', 0);
    const ranges = scanRanges(keptAssetRanges(row, keep, PRIMARY_ASSET), [], range(0, media.durationSec));
    const result = await run(engine.scanPrivacy(file, { kinds: scanKinds, precision: settings.privacy.precision, ranges }, (p) => p.phase === 'scan' && report('scan', p.ratio)));
    tracks = result.tracks;
  }

  const job = selectExportJob({
    media,
    recipe,
    soundOf: () => analysis,
    tracksOf: () => tracks,
    telemetry,
    places,
    screen: screen?.activity ?? null,
    notifications: screen?.notifications ?? [],
  });
  if (job.keepRanges.length === 0) throw new EngineError('empty-output');
  report('export', 0);
  return run(engine.exportVideo({ ...job, sources: { [PRIMARY_ASSET]: file }, camera: null, cameraBackdrop: null, outputName: outputFileName(file.name, output) }, (p) => report('export', p.ratio)));
}
